// Нов складов продукт направо от работен лист — „Доставка" и „Инвентаризация"
// (бутонът „Нов продукт" до търсачката и „Нов продукт „…“" при празен резултат).
//
// Полетата са СЪЩИТЕ като екрана „Продукти" на организацията (без частта „Като
// рецепта" — продаваемото не се създава от склада), защото записът отива в
// същата org таблица. ДДС е единственото задължително поле освен името.
//
// Шелът е общият (cloudts-design/references/modals.md), клас „Wide";
// Esc и клик на фона ги поема antd (`onCancel`) — без custom handler.
// С `onAfterClose` модалът НЕ връща фокуса на бутона, а казва на листа дали е
// създаден продукт — листът фокусира реда му (иначе antd връща фокуса на
// бутона СЛЕД анимацията и „краде" току-що фокусираната клетка).
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { App, Checkbox, Input, InputNumber, Modal, Select } from "antd";
import { AppstoreOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Product } from "../../../../api/inventory";
import {
  useCreateProduct,
  usePackagings,
  useUnits,
  useVats,
  useWarehouseGroups,
} from "../../../../hooks/useInventory";
import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../../../../utils/modalStyles";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (product: Product) => void;
  // Текстът от търсачката („няма го в списъка") — попълва името
  initialName?: string;
  // След анимацията на затваряне: true = продуктът е създаден. Подаден ли е,
  // фокусът остава на листа (без връщане на бутона-тригер).
  onAfterClose?: (created: boolean) => void;
};

export const ProductCreateModal = ({
  open,
  onClose,
  onCreated,
  initialName = "",
  onAfterClose,
}: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const createProduct = useCreateProduct();
  // Създаден ли е продукт при ТОВА отваряне (за `onAfterClose`)
  const createdRef = useRef(false);
  // Справочниците се теглят чак когато модалът се отвори.
  const { data: units = [] } = useUnits(open);
  const { data: groups = [] } = useWarehouseGroups(open);
  const { data: vats = [] } = useVats(open);
  // Разфасовките са вече в кеша на работния лист — hook-ът е без флаг.
  const { data: packagings = [] } = usePackagings();

  const [name, setName] = useState("");
  const [exportCode, setExportCode] = useState<number | null>(null);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [unitId, setUnitId] = useState<number | null>(null);
  const [vatId, setVatId] = useState<number | null>(null);
  const [minQuantity, setMinQuantity] = useState<number | null>(null);
  const [wastePct, setWastePct] = useState<number | null>(null);
  const [trackBatch, setTrackBatch] = useState(false);
  const [trackExpiry, setTrackExpiry] = useState(false);
  const [packagingIds, setPackagingIds] = useState<number[]>([]);
  const [nameError, setNameError] = useState<string | undefined>();
  const [unitError, setUnitError] = useState<string | undefined>();
  const [vatError, setVatError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      createdRef.current = false;
      return;
    }
    setName("");
    setExportCode(null);
    setGroupId(null);
    setUnitId(null);
    setVatId(null);
    setMinQuantity(null);
    setWastePct(null);
    setTrackBatch(false);
    setTrackExpiry(false);
    setPackagingIds([]);
    setNameError(undefined);
    setUnitError(undefined);
    setVatError(undefined);
  }, [open]);

  // ДДС е задължително → предпопълва се ставката по подразбиране от org-а.
  // Отваряне от търсачката → името е каквото е написано там
  useEffect(() => {
    if (open && initialName) setName(initialName);
  }, [open, initialName]);

  useEffect(() => {
    if (!open || vatId != null || vats.length === 0) return;
    setVatId((vats.find((vat) => vat.isDefault) ?? vats[0]).id);
  }, [open, vats, vatId]);

  // Етикетът е голото име и редът идва от бекенда (sort_index, после име) —
  // точно както изглежда падащото меню в екрана „Продукти" на org-а.
  const groupOptions = useMemo(
    () => groups.map((group) => ({ value: group.id, label: group.name })),
    [groups],
  );

  const handleSave = async () => {
    // Трите задължителни полета са същите като в org екрана: име, мерна
    // единица и ДДС. Останалите са по избор.
    const missingName = name.trim() ? undefined : t("inventoryPage.productNameRequired");
    const missingUnit =
      unitId == null ? t("inventoryPage.productUnitRequired") : undefined;
    const missingVat =
      vatId == null ? t("inventoryPage.productVatRequired") : undefined;
    setNameError(missingName);
    setUnitError(missingUnit);
    setVatError(missingVat);
    if (missingName || missingUnit || missingVat) return;
    try {
      const product = await createProduct.mutateAsync({
        name: name.trim(),
        vatId: vatId as number,
        exportCode,
        groupId,
        unitId,
        minQuantity,
        wastePct,
        trackBatch,
        trackExpiry,
        packagingIds,
      });
      message.success(t("inventoryPage.productSaved"));
      createdRef.current = true;
      onCreated(product);
      onClose();
    } catch (error) {
      // 409 (заето име / код) и 422 (несъществуваща връзка) носят готов текст.
      const detail = (error as { response?: { data?: { detail?: unknown } } })
        .response?.data?.detail;
      message.error(typeof detail === "string" ? detail : t("common.error"));
    }
  };

  const width = isMobile ? undefined : isTablet ? 520 : 640;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      focusTriggerAfterClose={!onAfterClose}
      afterClose={() => onAfterClose?.(createdRef.current)}
      width={width}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="bg-surface text-ink">
        <div
          className={`flex items-start gap-3 border-b border-shade-2 ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-5"
          }`}
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-brand-tint">
            <AppstoreOutlined className="text-[20px] text-brand-deep-ink" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 text-[18px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
              {t("inventoryPage.productAddTitle")}
            </h2>
            <p className="m-0 mt-1 text-[13px] font-medium leading-[1.4] text-body">
              {t("inventoryPage.productAddHint")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            className="flex h-[30px] w-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
          >
            <CloseOutlined className="text-[14px]" />
          </button>
        </div>

        {/* Скролва СЪДЪРЖАНИЕТО, не футърът: модалът е висок и иначе „Запази"
            изчезва под ръба на ниски екрани. */}
        <div
          className={`max-h-[min(62vh,520px)] overflow-y-auto ${
            isNarrow ? "px-4 pt-4" : "px-6 pt-5"
          }`}
        >
          {/* Подредбата, ширините и задължителните полета са 1:1 с екрана
              „Продукти" на организацията: Име(8) · Код(4) · Група(6) ·
              Единица(6) · ДДС(4) · Фира(4) · Мин. к-во(4), после двата
              чекбокса и накрая разфасовките. */}
          <div className="grid grid-cols-12 gap-x-3 gap-y-4">
            <Field
              label={t("inventoryPage.productName")}
              required
              error={nameError}
              className="col-span-12 sm:col-span-8"
            >
              <Input
                value={name}
                autoFocus
                maxLength={255}
                placeholder={t("inventoryPage.productNamePh")}
                status={nameError ? "error" : undefined}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError(undefined);
                }}
                onPressEnter={handleSave}
                className="!h-10"
              />
            </Field>

            <Field
              label={t("inventoryPage.productExportCode")}
              className="col-span-12 sm:col-span-4"
            >
              {/* `!w-full` — antd cssinjs е извън Tailwind layer-ите и без „!"
                  InputNumber остава с дефолтните си ~90px. */}
              <InputNumber
                value={exportCode}
                min={0}
                step={1}
                precision={0}
                onChange={(value) => setExportCode(value ?? null)}
                className="!h-10 !w-full [&_.ant-input-number-input]:!h-10"
              />
            </Field>

            <Field
              label={t("inventoryPage.productGroup")}
              className="col-span-12 sm:col-span-6"
            >
              <Select
                value={groupId ?? undefined}
                onChange={(value) => setGroupId((value as number) ?? null)}
                options={groupOptions}
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t("inventoryPage.productGroupPh")}
                className="!h-10 !w-full [&_.ant-select-selector]:!h-10"
              />
            </Field>

            <Field
              label={t("inventoryPage.productUnit")}
              required
              error={unitError}
              className="col-span-12 sm:col-span-6"
            >
              <Select
                value={unitId ?? undefined}
                onChange={(value) => {
                  setUnitId((value as number) ?? null);
                  setUnitError(undefined);
                }}
                options={units.map((unit) => ({
                  value: unit.id,
                  label: unit.name,
                }))}
                allowClear
                showSearch
                status={unitError ? "error" : undefined}
                optionFilterProp="label"
                placeholder={t("inventoryPage.productUnitPh")}
                className="!h-10 !w-full [&_.ant-select-selector]:!h-10"
              />
            </Field>

            <Field
              label={t("inventoryPage.productVat")}
              required
              error={vatError}
              className="col-span-12 sm:col-span-4"
            >
              <Select
                value={vatId ?? undefined}
                onChange={(value) => {
                  setVatId(value as number);
                  setVatError(undefined);
                }}
                options={vats.map((vat) => ({
                  value: vat.id,
                  label: `${vat.vatRate}%`,
                }))}
                showSearch
                status={vatError ? "error" : undefined}
                optionFilterProp="label"
                placeholder={t("inventoryPage.productVatPh")}
                className="!h-10 !w-full [&_.ant-select-selector]:!h-10"
              />
            </Field>

            <Field
              label={t("inventoryPage.productWastePct")}
              className="col-span-12 sm:col-span-4"
            >
              <InputNumber
                value={wastePct}
                min={0}
                max={100}
                step={0.1}
                addonAfter="%"
                onChange={(value) => setWastePct(value ?? null)}
                className="!w-full [&_.ant-input-number-input]:!h-10 [&_.ant-input-number]:!h-10"
              />
            </Field>

            <Field
              label={t("inventoryPage.productMinQuantity")}
              className="col-span-12 sm:col-span-4"
            >
              <InputNumber
                value={minQuantity}
                min={0}
                step={0.1}
                onChange={(value) => setMinQuantity(value ?? null)}
                className="!h-10 !w-full [&_.ant-input-number-input]:!h-10"
              />
            </Field>

            <Checkbox
              checked={trackBatch}
              onChange={(e) => setTrackBatch(e.target.checked)}
              className="col-span-12 !text-[13px] text-body sm:col-span-6"
            >
              {t("inventoryPage.productTrackBatch")}
            </Checkbox>
            <Checkbox
              checked={trackExpiry}
              onChange={(e) => setTrackExpiry(e.target.checked)}
              className="col-span-12 !text-[13px] text-body sm:col-span-6"
            >
              {t("inventoryPage.productTrackExpiry")}
            </Checkbox>

            <Field
              label={t("inventoryPage.productPackagings")}
              hint={t("inventoryPage.productPackagingsHint")}
              className="col-span-12"
            >
              <Select
                mode="multiple"
                value={packagingIds}
                onChange={(value) => setPackagingIds(value as number[])}
                options={packagings.map((packaging) => ({
                  value: packaging.id,
                  label: packaging.name,
                }))}
                showSearch
                optionFilterProp="label"
                placeholder={t("inventoryPage.productPackagingsPh")}
                className="!w-full"
              />
            </Field>
          </div>
        </div>

        <div
          className={`flex gap-2 border-t border-shade-2 ${
            isMobile ? "flex-col-reverse" : "justify-end"
          } ${isNarrow ? "p-4" : "px-6 py-4"}`}
        >
          <button
            type="button"
            onClick={onClose}
            className={`h-10 rounded-[10px] border border-shade-2 bg-transparent px-4 text-[14px] font-semibold text-body transition-colors duration-150 hover:bg-shade-1 ${
              isMobile ? "w-full" : ""
            }`}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={createProduct.isPending}
            className={`h-10 rounded-[10px] border-none bg-brand px-4 text-[14px] font-semibold text-white transition-colors duration-150 hover:bg-brand-deep disabled:cursor-not-allowed disabled:opacity-60 ${
              isMobile ? "w-full" : ""
            }`}
          >
            {t("common.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

function Field({
  label,
  required,
  error,
  hint,
  className,
  children,
}: {
  label: string;
  required?: boolean; // звездичка като при задължителните полета в org-а
  error?: string;
  hint?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={className}>
      <div className="mb-1 text-[10.5px] font-bold uppercase leading-[normal] tracking-[1.2px] text-muted">
        {label}
        {required ? <span className="ml-1 text-danger">*</span> : null}
      </div>
      {children}
      {error ? (
        <div className="mt-1 text-[11px] leading-snug text-danger">{error}</div>
      ) : hint ? (
        <div className="mt-1 text-[11px] leading-snug text-muted">{hint}</div>
      ) : null}
    </div>
  );
}

export default ProductCreateModal;
